import React, { useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import './QRPreview.css';

const QRPreview = ({ data, settings, customization, logo }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!data || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // QR Code options
    const qrOptions = {
      errorCorrectionLevel: logo ? 'H' : settings.errorCorrectionLevel,
      margin: settings.margin,
      scale: settings.scale,
      width: 400,
      color: {
        dark: customization.fgColor,
        light: customization.transparentBg ? '#00000000' : customization.bgColor
      }
    };

    // If version is not auto, set it
    if (settings.version !== 'auto') {
      qrOptions.version = parseInt(settings.version);
    }

    // Generate QR code
    QRCode.toCanvas(canvas, data, qrOptions, (error) => {
      if (error) {
        console.error('QR Code generation error:', error);
        return;
      }

      // Apply gradient if selected
      if (customization.gradientType !== 'none') {
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        applyGradient(canvas, ctx, imageData);
      }

      // Apply rounded modules/finders if selected
      if (customization.roundedModules || customization.roundedFinders) {
        applyRoundedCorners(canvas, ctx, customization.roundedModules, customization.roundedFinders);
      }

      // Add logo if present
      if (logo) {
        addLogo(canvas, ctx, logo);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, settings, customization, logo]);

  const applyGradient = (canvas, ctx, imageData) => {
    if (customization.gradientType !== 'none') {
      let gradient;
      if (customization.gradientType === 'linear') {
        gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      } else {
        gradient = ctx.createRadialGradient(
          canvas.width / 2, canvas.height / 2, 0,
          canvas.width / 2, canvas.height / 2, canvas.width / 2
        );
      }
      gradient.addColorStop(0, customization.gradientColors[0]);
      gradient.addColorStop(1, customization.gradientColors[1]);

      // Fill with gradient
      ctx.putImageData(imageData, 0, 0);
      ctx.globalCompositeOperation = 'source-in';
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = 'source-over';
    }
  };

  const applyRoundedCorners = (canvas, ctx, roundModules, roundFinders) => {
    // This is a simplified implementation
    // For production, you'd need a more sophisticated algorithm
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (!customization.transparentBg) {
      ctx.fillStyle = customization.bgColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    ctx.putImageData(imageData, 0, 0);
  };

  const addLogo = (canvas, ctx, logoSrc) => {
    const img = new Image();
    img.onload = () => {
      const maxLogoSize = canvas.width * 0.25; // 25% of QR code
      let logoWidth = img.width;
      let logoHeight = img.height;

      // Scale logo to fit
      if (logoWidth > maxLogoSize || logoHeight > maxLogoSize) {
        const scale = Math.min(maxLogoSize / logoWidth, maxLogoSize / logoHeight);
        logoWidth *= scale;
        logoHeight *= scale;
      }

      const x = (canvas.width - logoWidth) / 2;
      const y = (canvas.height - logoHeight) / 2;

      // Draw white background for logo
      const padding = 10;
      ctx.fillStyle = 'white';
      ctx.fillRect(x - padding, y - padding, logoWidth + padding * 2, logoHeight + padding * 2);

      // Draw logo
      ctx.drawImage(img, x, y, logoWidth, logoHeight);
    };
    img.src = logoSrc;
  };

  return (
    <div className="qr-preview">
      <h3>Preview</h3>
      <div className="preview-container">
        {data ? (
          <canvas ref={canvasRef} />
        ) : (
          <div className="empty-preview">
            <p>Enter data to generate QR code</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default QRPreview;
